/**
 * PlatformKpiCards
 * Four summary KPI cards: Conversations, Users, Input Tokens, Cost USD.
 * Each card shows a big number, a colored icon, and a trend badge
 * computed by splitting agentTimeseries into first/second halves.
 */

import { useMemo } from 'react';
import { Skeleton, Tooltip, theme } from 'antd';
import type { AgentStat, TenantRanking, TimeseriesPoint } from '../../types/insights';
import { useLanguage } from '@/hooks/useLanguage';
import conversationsIcon from '@/brand-assets/m2/General Icons/2thinking.png';
import usersIcon from '@/brand-assets/m2/General Icons/2people3.png';
import tokensIcon from '@/brand-assets/m2/General Icons/present2.png';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

interface TrendBadgeProps {
  pct: number | null;
}

function TrendBadge({ pct }: TrendBadgeProps) {
  if (pct === null || Math.abs(pct) < 1) {
    return (
      <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>—</span>
    );
  }
  const positive = pct >= 0;
  const display = Math.abs(pct) >= 10 ? Math.round(Math.abs(pct)) : Math.abs(pct).toFixed(1);
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color: positive ? '#16a34a' : '#dc2626' }}>
      {positive ? '↑' : '↓'} {display}%
    </span>
  );
}

// ─── Period trend computation ─────────────────────────────────────────────────

interface PeriodTrend {
  conversations: number | null;
  users: number | null;
  inputTokens: number | null;
}

function computeTrend(timeseries: TimeseriesPoint[]): PeriodTrend {
  const dates = Array.from(new Set(timeseries.map(p => p.date))).sort();
  if (dates.length < 2) return { conversations: null, users: null, inputTokens: null };

  const mid = Math.floor(dates.length / 2);
  const firstDates = new Set(dates.slice(0, mid));
  const secondDates = new Set(dates.slice(mid));

  const sum = (points: TimeseriesPoint[], field: keyof TimeseriesPoint) =>
    points.reduce((acc, p) => acc + (Number(p[field]) || 0), 0);

  const firstPoints = timeseries.filter(p => firstDates.has(p.date));
  const secondPoints = timeseries.filter(p => secondDates.has(p.date));

  const pct = (first: number, second: number) =>
    first > 0 ? ((second - first) / first) * 100 : null;

  return {
    conversations: pct(sum(firstPoints, 'conversations'), sum(secondPoints, 'conversations')),
    users: pct(sum(firstPoints, 'users'), sum(secondPoints, 'users')),
    inputTokens: pct(sum(firstPoints, 'input_tokens'), sum(secondPoints, 'input_tokens')),
  };
}

// ─── Single KPI card ──────────────────────────────────────────────────────────

interface KpiCardProps {
  iconSrc: string;
  label: string;
  description: string;
  value: string;
  trend: number | null;
  trendLabel: string;
  loading: boolean;
}

function KpiCard({ iconSrc, label, description, value, trend, trendLabel, loading }: KpiCardProps) {
  const { token } = theme.useToken();

  return (
    <div
      style={{
        flex: '1 1 0',
        minWidth: 160,
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        padding: '16px 20px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <img
          src={iconSrc}
          alt=""
          style={{
            width: 44,
            height: 44,
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 13, color: token.colorTextSecondary, fontWeight: 500 }}>{label}</span>
          <div style={{ fontSize: 11, color: token.colorTextTertiary, marginTop: 2, lineHeight: 1.3 }}>{description}</div>
        </div>
      </div>
      {loading ? (
        <Skeleton.Input active size="large" style={{ width: 100, height: 32, borderRadius: 6 }} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: 28, fontWeight: 700, color: token.colorText, lineHeight: 1 }}>
            {value}
          </span>
          <Tooltip title={trendLabel}>
            <span><TrendBadge pct={trend} /></span>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface PlatformKpiCardsProps {
  agentStats: AgentStat[];
  agentTimeseries: TimeseriesPoint[];
  tenantRankings: TenantRanking[];
  tenantTimeseries: TimeseriesPoint[];
  loading: boolean;
}

export function PlatformKpiCards({ agentStats, agentTimeseries, tenantRankings, tenantTimeseries, loading }: PlatformKpiCardsProps) {
  const { t } = useLanguage();

  const totals = useMemo(() => ({
    conversations: agentStats.reduce((s, a) => s + a.total_conversations, 0),
    // Sum active_users from tenantRankings — users belong to exactly one tenant, no double-counting
    users: tenantRankings.reduce((s, r) => s + r.active_users, 0),
    inputTokens: agentStats.reduce((s, a) => s + a.total_input_tokens, 0),
  }), [agentStats, tenantRankings]);

  const agentTrend = useMemo(() => computeTrend(agentTimeseries), [agentTimeseries]);
  const tenantTrend = useMemo(() => computeTrend(tenantTimeseries), [tenantTimeseries]);
  const trend = useMemo(() => ({
    conversations: agentTrend.conversations,
    users: tenantTrend.users,        // use tenant timeseries for accurate user trend
    inputTokens: agentTrend.inputTokens,
  }), [agentTrend, tenantTrend]);

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      <KpiCard
        iconSrc={conversationsIcon}
        label={t('insights.pulse.conversations')}
        description={t('insights.pulse.kpiConversations')}
        value={formatNumber(totals.conversations)}
        trend={trend.conversations}
        trendLabel={t('insights.pulse.trendVsFirstHalf')}
        loading={loading}
      />
      <KpiCard
        iconSrc={usersIcon}
        label={t('insights.pulse.users')}
        description={t('insights.pulse.kpiUsers')}
        value={formatNumber(totals.users)}
        trend={trend.users}
        trendLabel={t('insights.pulse.trendVsFirstHalf')}
        loading={loading}
      />
      <KpiCard
        iconSrc={tokensIcon}
        label={t('insights.pulse.tokens')}
        description={t('insights.pulse.kpiInputTokens')}
        value={formatNumber(totals.inputTokens)}
        trend={trend.inputTokens}
        trendLabel={t('insights.pulse.trendVsFirstHalf')}
        loading={loading}
      />
    </div>
  );
}
